import { ChangeEvent, useRef } from 'react';
import Image from 'next/image';
import BannerDefault from '@/assets/img/BannerDefault.svg';
import { useUser } from '@/contexts/AuthContext';
import { useInstitutionalPage } from '@/contexts/InstitutionalPageContext';
import { useLoading } from '@/contexts/LoadingContext';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { ICompany } from '@/types/api/companies';
import { canEditCompanyInstitutionalPage } from '@/utils/components/institutionalPage';
import {
  ALLOWED_IMAGE_FILE_TYPES,
  MAXIMUM_IMG_SIZE_ALLOWED,
} from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { Avatar, Badge, BadgeProps, Box, IconButton } from '@mui/material';
import type { IconButtonProps } from '@mui/material';

interface ICustomBadgeProps extends BadgeProps {
  badgeContentProps: IconButtonProps & { 'data-testid': string };
  canEdit?: boolean;
}

const CustomBadge = ({
  children,
  badgeContentProps,
  canEdit,
  ...rest
}: ICustomBadgeProps) => {
  return (
    <Badge
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      badgeContent={
        canEdit && (
          <IconButton
            size="small"
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid currentColor',
              color: 'rgba(0, 0, 0, 0.3)',
              '&:hover': {
                backgroundColor: '#f3f3f3',
              },
            }}
            {...badgeContentProps}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )
      }
      overlap="circular"
      {...rest}
    >
      {children}
    </Badge>
  );
};

export const Banner = () => {
  const { setIsLoading } = useLoading();
  const { user, updateUserData } = useUser();
  const { company, updateCompany } = useInstitutionalPage();

  const editLogoRef = useRef<HTMLInputElement | null>(null);
  const editBannerRef = useRef<HTMLInputElement | null>(null);

  const userCanEdit = canEditCompanyInstitutionalPage(company, user);

  const editCompany = async (event: ChangeEvent<HTMLInputElement>) => {
    const [companyUpdated, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const image = validateImage(event.target.files?.[0]);

      const formData = new FormData();
      formData.append(event.target.name, image);
      formData.append('full_name', company.full_name);

      const { data } = await api.patch<ICompany>(
        `api/companies/${company.id}/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return data;
    }).finally(() => setIsLoading(false));

    resetInput(event.target);

    if (error) return Toast('error', error.message);

    updateCompany(companyUpdated);
    updateUserData({
      memberships: user.memberships?.map(membership =>
        membership.company.id === companyUpdated.id
          ? {
              ...membership,
              company: companyUpdated,
            }
          : membership
      ),
    });
  };

  const validateImage = (image: File | undefined) => {
    if (!image) {
      throw Error('Arquivo não encontrado!');
    }

    if (image.size >= MAXIMUM_IMG_SIZE_ALLOWED) {
      throw Error('Tamanho máximo de arquivo excedido (30 mb)');
    }

    if (!ALLOWED_IMAGE_FILE_TYPES.includes(image.type)) {
      throw Error('Formato não suportado! (São permitidos: PNG, JPG, JPEG)');
    }
    return image;
  };

  const resetInput = (input: HTMLInputElement | null) => {
    if (input) {
      input.value = '';
    }
  };

  return (
    <Box
      sx={theme => ({
        maxWidth: theme.breakpoints.values['md'],
        margin: '0 auto',
      })}
    >
      <CustomBadge
        badgeContentProps={{
          'aria-label': 'Editar banner da página institucional',
          'data-testid': 'button-edit-banner',
          onClick: () => editBannerRef.current?.click(),
        }}
        canEdit={userCanEdit}
        sx={{
          width: '100%',
          '& > .MuiBadge-badge': {
            bottom: 'calc(32px / 2 + 12px)',
            right: 'calc(32px / 2 + 12px)',
          },
        }}
      >
        <input
          onChange={editCompany}
          name="background_image"
          accept={'image/png, image/jpg, image/jpeg'}
          ref={editBannerRef}
          className="sr-only"
          type="file"
          placeholder="Adicione uma capa para sua empresa"
        />
        <Box
          component="section"
          sx={theme => ({
            alignItems: 'flex-end',
            display: 'flex',
            height: '100px',
            justifyContent: 'center',
            paddingX: 2,
            width: '100%',
            position: 'relative',
            [theme.breakpoints.up('md')]: {
              height: '200px',
              justifyContent: 'flex-start',
              margin: '0 auto',
            },
          })}
        >
          <Image
            alt={`${company.display_name} capa`}
            layout="fill"
            objectFit="cover"
            priority
            src={company.background_image || BannerDefault.src}
          />
          <CustomBadge
            badgeContentProps={{
              'aria-label': 'Editar logo da página institucional',
              'data-testid': 'button-edit-logo',
              onClick: () => editLogoRef.current?.click(),
            }}
            canEdit={userCanEdit}
            sx={{
              marginBottom: '-40px',
            }}
          >
            <input
              onChange={editCompany}
              name="logo"
              accept={'image/png, image/jpg, image/jpeg'}
              ref={editLogoRef}
              className="sr-only"
              type="file"
              placeholder="Adicione uma logo para sua empresa"
            />
            <Avatar
              alt={`Logo da empresa ${company.full_name}`}
              src={company.logo}
              sx={{
                backgroundColor: 'background.paper',
                filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.25))',
                height: '98px',
                width: '98px',
              }}
            >
              <StorefrontIcon
                sx={{
                  color: '#C6C6C6',
                  height: '36px',
                  width: '40px',
                }}
              />
            </Avatar>
          </CustomBadge>
        </Box>
      </CustomBadge>
    </Box>
  );
};
